import { useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { usePracticeAnalyticsShallowStore } from './use-practice-analytics-store';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      recapturedPatients: scheduledPatients {
        historicalData(start: $start, end: $end, step: $step) {
          label
          leadTime
          production
          timestamp
          total
        }
        totals(start: $start, end: $end, step: $step) {
          leadTime
          patients
          production
        }
      }
    }
  }
`;

export const useGetRecapturedPatientsMetric = () => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');

  const { demoData, filters, isDemoAccount } = usePracticeAnalyticsShallowStore('demoData', 'filters', 'isDemoAccount');

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `recapturedPatientsSummary-${isDemoAccount}-${JSON.stringify(filters)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.RecapturedPatientsTrend>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.recapturedPatients : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { aggregatedData, rawData } = useMemo(() => {
    return PracticeAnalyticsAggregations.recapturedPatientsSummary(data);
  }, [data]);

  return {
    aggregatedRecapturedPatientsData: aggregatedData,
    isLoading,
    rawRecapturedPatientsData: rawData,
  };
};
